/**
 * AuthScreen — web sign-in.
 *
 * Email magic-link is the primary path. Apple Sign-In is NOT shown on
 * the web tier: Apple Guideline 4.8 requires Sign in with Apple on iOS
 * apps that offer another social login, but web apps are not covered
 * by that rule. The iOS app retains its Apple Sign-In button.
 *
 * Google sign-in (via Supabase OAuth) is offered as a secondary
 * option. Magic-link remains primary because it has the lowest
 * drop-off rate and zero third-party-callback complexity.
 *
 * Resend-backed email delivery is wired on the Supabase dashboard
 * side per docs/WEB_APP_PLAN_2026-07-17.md §5 — the magic-link
 * arrives in <30s.
 */
import { Suspense, lazy, useState } from 'react';
import type { ReactElement } from 'react';
import { useAuth } from '../lib/auth/useAuth';
import { BrandMonogram } from '../lib/effectiveslp/BrandMonogram';

// Dev shortcut lives in screens/_dev/DevSignIn.tsx — production builds
// tree-shake the dynamic import (see web/src/screens/_dev/README.md).
const DevSignIn = import.meta.env.DEV
  ? lazy(() => import('./_dev/DevSignIn'))
  : null;

type Status = 'idle' | 'sending' | 'sent' | 'google-pending' | 'error';

interface Props {
  initialEmail?: string;
  onSignedIn?: () => void;
}

export function AuthScreen({
  initialEmail = '',
  onSignedIn,
}: Props): ReactElement {
  const [email, setEmail] = useState(initialEmail);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const { signInWithEmail, signInWithGoogle } = useAuth();

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter your email address.');
      return;
    }
    setStatus('sending');
    setError(null);
    try {
      await signInWithEmail({ email: trimmed });
      setStatus('sent');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not send magic link.';
      setError(message);
      setStatus('error');
    }
  }

  async function handleGoogle(): Promise<void> {
    setStatus('google-pending');
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Google sign-in is not available yet (Google provider needs to be enabled in Supabase).';
      setError(message);
      setStatus('idle');
    }
  }

  if (status === 'sent') {
    return (
      <main className="screen auth">
        <div className="auth-hero">
          <BrandMonogram size={64} />
          <header className="screen-header">
            <h1>Check your email</h1>
            <p className="tagline">We sent a sign-in link to {email.trim()}.</p>
          </header>
        </div>

        <section className="auth-confirm" aria-live="polite">
          <p>
            Open the email on this device and tap the link to finish
            signing in. The link expires in 1 hour.
          </p>
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setStatus('idle');
              setError(null);
            }}
          >
            Use a different email
          </button>
          {onSignedIn && (
            <button
              type="button"
              className="link-button"
              onClick={() => onSignedIn()}
            >
              I&rsquo;ve signed in — continue
            </button>
          )}
        </section>
      </main>
    );
  }

  return (
    <main className="screen auth">
      <div className="auth-hero">
        <BrandMonogram size={64} />
        <header className="screen-header">
          <h1>Sign in</h1>
          <p className="tagline">
            We&rsquo;ll email you a one-tap sign-in link.
          </p>
        </header>
      </div>

      <form className="auth-form" onSubmit={(e) => void handleSubmit(e)}>
        <label>
          Email
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === 'sending'}
            required
          />
        </label>

        <button
          type="submit"
          className="snap-button"
          disabled={status === 'sending'}
        >
          {status === 'sending' ? 'Sending…' : 'Send magic link'}
        </button>

        {error && <p className="error">{error}</p>}
      </form>

      <div className="auth-divider">
        <span>or</span>
      </div>

      <button
        type="button"
        className="auth-google"
        onClick={() => void handleGoogle()}
        disabled={status === 'sending' || status === 'google-pending'}
      >
        {status === 'google-pending' ? 'Opening Google…' : 'Continue with Google'}
      </button>

      <p className="tagline">
        On the iOS app, Sign in with Apple is also available.
      </p>

      {DevSignIn && (
        <Suspense fallback={null}>
          {onSignedIn ? (
            <DevSignIn
              parentBusy={status === 'sending' || status === 'google-pending'}
              onSignedIn={onSignedIn}
            />
          ) : (
            <DevSignIn
              parentBusy={status === 'sending' || status === 'google-pending'}
            />
          )}
        </Suspense>
      )}
    </main>
  );
}